/**
 * Working-day arithmetic for SIP scheduling (§10, feature 3).
 *
 * Dates here are plain `YYYY-MM-DD` strings in IST terms, never `Date`
 * objects with a time component. A SIP installment belongs to a calendar day,
 * not an instant, and threading timezones through it is how an installment
 * ends up dated the 4th for someone in London and the 5th in Mumbai.
 *
 * Pure and I/O-free like the rest of this package.
 */

/** `YYYY-MM-DD`. */
export type IsoDate = string;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string' || !DATE_RE.test(value)) return false;
  // Reject the shapes that pass the regex but are not real days — 2026-02-31
  // would otherwise roll forward silently into March.
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1) return false;
  return d <= daysInMonth(y, m);
}

export function daysInMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

/** Parse to a UTC-midnight Date. Throws on anything not a real date. */
export function parseIsoDate(value: IsoDate): Date {
  if (!isIsoDate(value)) throw new RangeError(`Not a calendar date: ${String(value)}`);
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

export function toIsoDate(date: Date): IsoDate {
  return date.toISOString().slice(0, 10);
}

/** Unix-seconds instant to the IST calendar day it falls on. */
export function istDateFromEpoch(epochSec: number): IsoDate {
  // IST is UTC+5:30 with no daylight saving, so a fixed offset is exact here
  // rather than an approximation.
  return new Date((epochSec + 5.5 * 3600) * 1000).toISOString().slice(0, 10);
}

export function addDays(value: IsoDate, days: number): IsoDate {
  const date = parseIsoDate(value);
  date.setUTCDate(date.getUTCDate() + days);
  return toIsoDate(date);
}

/** 0 = Sunday. */
export function dayOfWeek(value: IsoDate): number {
  return parseIsoDate(value).getUTCDay();
}

export function isWeekend(value: IsoDate): boolean {
  const day = dayOfWeek(value);
  return day === 0 || day === 6;
}

/**
 * Indian market holidays, as data rather than as a rule.
 *
 * There is no free, stable API for the NSE/BSE holiday calendar, and a scraper
 * that silently broke would be worse than a list that is visibly out of date —
 * the same call §6 makes about India CPI. So this is maintained by hand and
 * `holidaysKnownThrough` states plainly how far it goes. Past that point
 * `isWorkingDay` still handles weekends and the NAV lookup is what actually
 * decides: an installment is never priced off a date AMFI published nothing
 * for, whatever this list says.
 *
 * Dates are the NSE trading holidays; AMFI does not publish a NAV on them.
 */
export const MARKET_HOLIDAYS: readonly IsoDate[] = [
  // 2026
  '2026-01-26', // Republic Day
  '2026-03-03', // Holi
  '2026-03-21', // Id-ul-Fitr
  '2026-04-01', // Annual bank closing
  '2026-04-03', // Good Friday
  '2026-04-14', // Dr. Ambedkar Jayanti
  '2026-05-01', // Maharashtra Day
  '2026-05-27', // Bakri Id
  '2026-08-15', // Independence Day
  '2026-09-14', // Ganesh Chaturthi
  '2026-10-02', // Gandhi Jayanti
  '2026-10-20', // Dussehra
  '2026-11-09', // Diwali Balipratipada
  '2026-11-24', // Guru Nanak Jayanti
  '2026-12-25', // Christmas
  // 2027
  '2027-01-26',
  '2027-03-22',
  '2027-03-26',
  '2027-04-14',
  '2027-05-01',
  '2027-08-15',
  '2027-10-02',
  '2027-12-25',
] as const;

const HOLIDAY_SET = new Set(MARKET_HOLIDAYS);

/** The last date the hand-maintained holiday list covers. */
export const holidaysKnownThrough: IsoDate = '2027-12-31';

export function isMarketHoliday(value: IsoDate): boolean {
  return HOLIDAY_SET.has(value);
}

export function isWorkingDay(value: IsoDate): boolean {
  return !isWeekend(value) && !isMarketHoliday(value);
}

/**
 * The next working day strictly after `value`.
 *
 * Bounded rather than unbounded: a corrupt holiday list should not spin. Ten
 * days is far beyond the longest run of Indian market closures.
 */
export function nextWorkingDay(value: IsoDate): IsoDate {
  let candidate = addDays(value, 1);
  for (let i = 0; i < 10; i++) {
    if (isWorkingDay(candidate)) return candidate;
    candidate = addDays(candidate, 1);
  }
  return candidate;
}

/** `value` itself when it is a working day, otherwise the next one. */
export function onOrNextWorkingDay(value: IsoDate): IsoDate {
  return isWorkingDay(value) ? value : nextWorkingDay(value);
}

/**
 * The date a SIP due on `dayOfMonth` falls in a given month.
 *
 * A day past the end of a short month lands on the last day of it rather than
 * rolling into the next: a SIP set for the 31st should debit in February, not
 * skip it. The caller applies `onOrNextWorkingDay` afterwards.
 */
export function sipDateFor(year: number, month1: number, dayOfMonth: number): IsoDate {
  const day = Math.min(Math.max(1, Math.floor(dayOfMonth)), daysInMonth(year, month1));
  return `${year}-${String(month1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Every SIP installment date strictly after `after` and on or before `through`.
 *
 * Returns the *due* dates, unadjusted for weekends — the caller decides how to
 * price them, because "which day was this due" and "which NAV applies" are
 * different questions and conflating them loses the audit trail.
 */
export function dueDatesBetween(
  dayOfMonth: number,
  after: IsoDate | null,
  through: IsoDate,
  maxDates = 240,
): IsoDate[] {
  const end = parseIsoDate(through);
  const out: IsoDate[] = [];

  // Start from the month after the last one applied, or the month of `through`
  // when nothing has ever been applied — a brand-new SIP takes its next
  // installment, never a backdated run of them.
  const start = after ? parseIsoDate(after) : end;
  let year = start.getUTCFullYear();
  let month = start.getUTCMonth() + 1;

  for (let i = 0; i < maxDates; i++) {
    const candidate = sipDateFor(year, month, dayOfMonth);
    if (parseIsoDate(candidate) > end) break;
    if (!after || candidate > after) out.push(candidate);

    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return out;
}
